/**
 * Compatibility redirect for the retired standalone student exam profile.
 *
 * `/team-leader/exams/student/$studentId` → `/team-leader/students/$studentId?tab=exams`
 *
 * The old page duplicated the canonical student profile from the same endpoint
 * (`GET /teamleader/exams/students/{id}/profile`) and rendered it a second, divergent way. Parity
 * was established before this file became a redirect: its summary stats, exam history,
 * certificates, checkpoint progress, course/track lists, category performance and activity log all
 * already existed on the canonical page, and its one genuinely unique capability — the
 * server-paginated attempt timeline with its exam / event-type / date-range filters and per-row
 * moderation actions — was ported to `components/team-leader/exams/StudentAttemptTimeline.tsx` and
 * mounted in that page's Exams tab.
 *
 * **Exam context.** The old URL is student-scoped and carries no exam id — there is nothing to
 * forward to the workspace's `?tab=attempts&student=` form, and the route never declared
 * `validateSearch`, so no bookmark can be carrying one either. `?tab=exams` on the canonical page
 * is therefore the closest-matching destination, and it is exactly where the ported timeline lives.
 *
 * **Authorization is unchanged.** Both routes sit under `_authenticated`, and both read the same
 * TeamLeader-scoped endpoints; the redirect moves the caller between two pages with identical
 * server-side scoping rather than widening anything. No loop is possible: the target is a different
 * route, and it never redirects back.
 *
 * **This file stays.** Bookmarks and links in old messages cost nothing to honour.
 */
import { createFileRoute, redirect } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/team-leader/exams_/student/$studentId")({
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/team-leader/students/$studentId",
      params: { studentId: params.studentId },
      search: { tab: "exams" as const },
      replace: true,
    });
  },
});
